import { useRef, useState } from "react";
import { UploadCloud, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const OK = [".pdf", ".docx", ".txt"];

export const validCvFile = (file) => {
  if (!file) return "Choose a file first.";
  if (!OK.some((e) => file.name.toLowerCase().endsWith(e))) return "Please choose a PDF, Word (.docx) or text file.";
  if (file.size > 8 * 1024 * 1024) return "That file is over 8MB. Please choose a smaller one.";
  return "";
};

export default function CvUploader({ onUploaded, compact = false, testid = "cv-upload-dropzone", label }) {
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const ref = useRef(null);

  const upload = async (file) => {
    const err = validCvFile(file);
    if (err) return toast.error(err);
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/cv/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Your CV is saved. You won't need to upload it again.");
      onUploaded?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload didn't work. Please try again.");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div data-testid={testid} role="button" tabIndex={0} aria-label="Upload your CV"
      onClick={() => !busy && ref.current?.click()}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && ref.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); upload(e.dataTransfer.files?.[0]); }}
      className={`cursor-pointer rounded-2xl border-2 border-dashed text-center transition-colors ${compact ? "p-6" : "p-8 sm:p-12"} ${over ? "border-brand bg-brand-soft" : "border-line-strong/50 bg-surface hover:border-brand/60 hover:bg-brand-soft/40"}`}>
      <input ref={ref} data-testid="cv-file-input" type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      <div className="w-12 h-12 mx-auto rounded-xl bg-brand-soft flex items-center justify-center mb-3">
        {busy ? <Loader2 className="w-6 h-6 text-brand animate-spin" /> : compact ? <FileText className="w-6 h-6 text-brand" /> : <UploadCloud className="w-6 h-6 text-brand" />}
      </div>
      <p className="font-semibold text-ink">{busy ? "Reading your CV…" : label || "Drop your CV here or tap to choose a file"}</p>
      <p className="text-sm text-faint mt-1">PDF, Word or text · up to 8MB</p>
    </div>
  );
}
